import React from 'react';
import { useIntl } from 'react-intl';
import styled from '@emotion/styled';
import { Button, CircularProgress } from '@mui/material';
import { Body1, BoldText, Body2 } from '../../../../../layouts/TextStyles';
import CustomInput from '../../../../../components/muiComponents/customInput/CustomInput';
import OnlineIcon from '../../../../../assets/icons/star-icon.svg';
import VideoIcon from '../../../../../assets/icons/video-icon.svg';

interface CheckoutSidebarProps {
    checkingPromocode?: boolean;
    loadingBasket?: boolean;
    buying?: boolean;
    formik: any;
    course: any;
    courseSubjects?: any[];
    paymentType?: string;
    promocode?: string;
    setPromocode: (value: string) => void;
    checkPromocode: () => void;
}

export default function CheckoutSidebar({
    checkingPromocode = false,
    loadingBasket = false,
    buying = false,
    formik,
    course,
    courseSubjects = [],
    paymentType = 'yearly',
    promocode = '',
    setPromocode,
    checkPromocode
}: CheckoutSidebarProps) {
    const { formatMessage, locale } = useIntl();

    const getWithoutDiscount = () => {
        if (!course) return '0';
        if (course?.videoCourseType === "VIDEO_ONLY") {
            if (paymentType === 'yearly') return (course?.yearlyWithoutDiscount || 0)?.toLocaleString();
            return (course?.monthlyWithoutDiscount || 0)?.toLocaleString();
        }
        if (course?.videoCourseType === "COURSE" && course?.monthly) return (course?.monthlyWithoutDiscount || 0)?.toLocaleString();
        if (course?.videoCourseType === "COURSE" && course?.yearly) return (course?.yearlyWithoutDiscount || 0)?.toLocaleString();
        return '0';
    };

    const getDiscount = () => {
        if (!course) return 0;
        if (course?.videoCourseType === "VIDEO_ONLY") {
            if (paymentType === 'yearly') return Math.max((course?.yearlyWithoutDiscount || 0) - (course?.yearly || 0), 0);
            return Math.max((course?.monthlyWithoutDiscount || 0) - (course?.monthly || 0), 0);
        }
        if (course?.videoCourseType === "COURSE" && course?.monthly) return Math.max((course?.monthlyWithoutDiscount || 0) - (course?.monthly || 0), 0);
        if (course?.videoCourseType === "COURSE" && course?.yearly) return Math.max((course?.yearlyWithoutDiscount || 0) - (course?.yearly || 0), 0);
        return 0;
    };

    const getTotal = () => {
        if (!course) return '0';
        if (course?.videoCourseType === "VIDEO_ONLY") {
            if (paymentType === 'yearly') {
                if (course?.promocodeValid) return (course?.yearlyAfterPromocode || 0)?.toLocaleString();
                return (course?.yearly || 0)?.toLocaleString();
            }

            if (course?.promocodeValid) return (course?.monthlyAfterPromocode || 0)?.toLocaleString();
            return (course?.monthly || 0)?.toLocaleString();
        }
        if (course?.videoCourseType === "COURSE" && course?.monthly) {
            if (course?.promocodeValid) return (course?.monthlyAfterPromocode || 0)?.toLocaleString();
            return (course?.monthly || 0)?.toLocaleString();
        }
        if (course?.videoCourseType === "COURSE" && course?.yearly) {
            if (course?.promocodeValid) return (course?.yearlyAfterPromocode || 0)?.toLocaleString();
            return (course?.yearly || 0)?.toLocaleString();
        }
        return '0';
    };

    const getPromocodeDiscount = () => {
        if (!course || !course?.promocodeValid) return 0;
        if (course?.videoCourseType === "VIDEO_ONLY") {
            if (paymentType === 'yearly') return Math.max((course?.yearly || 0) - (course?.yearlyAfterPromocode || 0), 0);
            return Math.max((course?.monthly || 0) - (course?.monthlyAfterPromocode || 0), 0);
        }

        if (course?.videoCourseType === "COURSE" && course?.monthly) return Math.max((course?.monthly || 0) - (course?.monthlyAfterPromocode || 0), 0);
        if (course?.videoCourseType === "COURSE" && course?.yearly) return Math.max((course?.yearly || 0) - (course?.yearlyAfterPromocode || 0), 0);
        return 0;
    };

    return (
        <SidebarContainer>
            <PriceContainer>
                {loadingBasket && <CircularProgress />}

                <>
                    <CourseList>
                        {courseSubjects.map((courseSubject, index) => (
                            <CourseItem key={index}>
                                <CourseInfo>
                                    <BoldText color="#434343">
                                        {locale?.toLowerCase()?.startsWith('kz')
                                            ? (courseSubject?.subject?.titleKz || courseSubject?.subject?.titleRu)
                                            : (courseSubject?.subject?.titleRu || courseSubject?.subject?.titleKz)}
                                    </BoldText>
                                </CourseInfo>
                                <PriceInfo>
                                    <CourseTag>
                                        <CourseIcon
                                            src={course?.videoCourseType === 'COURSE' ? OnlineIcon : VideoIcon}
                                            alt={course?.videoCourseType === 'COURSE' ? 'online' : 'video'}
                                        />
                                        <Body2 color="#858D9D">
                                            {formatMessage({ id: `courseType-${course?.videoCourseType}` })}
                                        </Body2>
                                    </CourseTag>
                                </PriceInfo>
                            </CourseItem>
                        ))}
                    </CourseList>

                    <PriceSummary>
                        <SummaryRow>
                            <Body1 color="#858D9D">{formatMessage({ id: 'base_price' })}</Body1>
                            <BoldText color="#333843">{getWithoutDiscount()} ₸</BoldText>
                        </SummaryRow>

                        {getDiscount() > 0 && (
                            <SummaryRow>
                                <Body1 color="#858D9D">{formatMessage({ id: 'discount' })}</Body1>
                                <BoldText color="#ED4FA7">
                                    {getDiscount()?.toLocaleString()} ₸
                                </BoldText>
                            </SummaryRow>
                        )}

                        {getPromocodeDiscount() > 0 && (
                            <SummaryRow>
                                <Body1 color="#858D9D">Промокод</Body1>
                                <BoldText color="#ED4FA7">
                                    {getPromocodeDiscount()?.toLocaleString()} ₸
                                </BoldText>
                            </SummaryRow>
                        )}

                        <PromocodeBox>
                            <CustomInput
                                name="promocode"
                                value={promocode}
                                onChange={(e) => setPromocode(e.target.value)}
                                placeholder="Промокод"
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                type="button"
                                sx={{ height: '50px' }}
                                disabled={checkingPromocode}
                                onClick={checkPromocode}
                            >
                                {checkingPromocode ? <CircularProgress size={16} /> : formatMessage({ id: 'check' })}
                            </Button>
                        </PromocodeBox>

                        <SummaryRow $isTotal>
                            <Body1 color="#333843">{formatMessage({ id: 'total' })}</Body1>
                            <BoldText color="#333843">{getTotal()} ₸</BoldText>
                        </SummaryRow>
                    </PriceSummary>

                    <Button
                        variant="contained"
                        color="primary"
                        type="button"
                        disabled={buying}
                        onClick={() => formik?.submitForm?.()}
                    >
                        {formatMessage({ id: 'pay_button' })}
                    </Button>
                </>
            </PriceContainer>
        </SidebarContainer>
    );
}

// Styled Components
const SidebarContainer = styled.aside`
  width: 400px;
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 24px;
  
  @media (max-width: 1024px) {
    width: 100%;
    position: static;
  }
`;

const PriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CourseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CourseItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const CourseInfo = styled.div`
  flex: 1;
`;

const PriceInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

const CourseTag = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CourseIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const PriceSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const SummaryRow = styled.div<{ $isTotal?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.$isTotal ? '12px 0' : '8px 0'};
  border-bottom: ${props => props.$isTotal ? '2px solid #e0e0e0' : '1px solid #e0e0e0'};
  font-weight: ${props => props.$isTotal ? '600' : 'normal'};
  
  &:last-child {
    border-bottom: none;
  }
`;

const PromocodeBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

